import { bookingStart, readDate, whenBlock } from './booking'
import { bookingPurpose, plainWord, PURPOSE_LABEL, routeEnds, type BookingPurpose } from './ticketWords'
import { rechecked } from './verify'
import { withoutTextSlides } from './textSlides'
import type { IconName } from '../components/Icon'
import type { ItemKind, ReadItem } from './read'
import type { CleanupRun } from './runs'

/**
 * What an unpacked note shows on its card in Browse (docs/screenshot-cleanup/PLAN.md, decision 18): what it is, at a glance, and the
 * most useful title for that kind. A recipe by its dish, a ticket by its event and day, a post by its first sentence (a quote card by
 * its quote), with who posted it where. Read from the note's newest run, never written into the note; an undone unpack shows none.
 */
export type CardKind = ItemKind | 'quote'
export interface RouteEnd { code: string | null; place: string | null }
export interface UnpackedCard {
  kind: CardKind
  /** The kind in a word or two: "Recipe", "Ticket", "Quote", "Instagram post". */
  label: string
  title: string | null
  /** Who posted it and where, or a booking's day and time. */
  detail: string | null
  platform: string | null
  /** What a booking is for, when its words say (a boarding pass, a train ticket), for its icon. */
  purpose?: BookingPurpose | null
  /** The card's lead as the preview shows it (design/studio/unpacked-cards.css): a booking's name (a route's day and time) over one line (the venue, the day and hour). */
  heading?: string | null
  line?: string | null
  /** A flight's or train's ends, large on the card's cover: a code ("BER") over its place, or the place alone. */
  route?: { from: RouteEnd; to: RouteEnd } | null
  /** When a booking starts (local time), for the urgency pill. */
  startsAt?: number | null
  /** The first words of a post, article or plain saved text, for a preview that is its content. */
  excerpt?: string | null
  /** Saved text with no kind: shown as a plain note, with no cover or strip. */
  plain?: boolean
  /** Everything the note's screenshots say (every item's words, those on its pictures too, title, author, a booking's fields), for search. */
  words: string
}

const LABEL: Record<CardKind, string> = { post: 'Post', article: 'Article', recipe: 'Recipe', ticket: 'Ticket', reservation: 'Reservation', appointment: 'Appointment', other: 'Saved text', quote: 'Quote' }
const MAX_TITLE = 80
const pictograph = /[\p{Extended_Pictographic}️‍]/gu

const IATA = /\b[A-Z]{3}\b/
/** One end of a route: "BER Terminal 1" is code BER over "Terminal 1"; a station ("Berlin Hbf") has no code and is shown as its name. */
function routeEnd(text: string): RouteEnd {
  const code = IATA.exec(text)?.[0] ?? null
  const place = (code ? text.replace(code, '') : text).replace(/\s+/g, ' ').trim()
  return code ? { code, place: place || null } : { code: null, place: place || text }
}

/** A title as a person would write it: no emoji, Markdown marks or list number ("1. Your 40s…"), "SCARPACCIA" as "Scarpaccia", cut at a word. */
export function tidyTitle(text: string): string {
  let title = text.replace(pictograph, '').replace(/^[#>*\-\s]+/, '').replace(/^\d{1,3}[.)]\s+/, '').replace(/[*_`]/g, '').replace(/\s+/g, ' ').trim()
  if (title.length > 3 && title === title.toUpperCase() && /\p{L}/u.test(title)) title = title.toLowerCase().replace(/(^|[\s(-])(\p{L})/gu, (_, gap: string, letter: string) => gap + letter.toUpperCase())
  if (title.length <= MAX_TITLE) return title
  const cut = title.slice(0, MAX_TITLE + 1)
  return `${cut.slice(0, Math.max(cut.lastIndexOf(' '), MAX_TITLE * 0.6)).replace(/[\s,;:–-]+$/, '')}…`
}

/** The first sentence of some words: their first paragraph, its wrapped lines run together, up to its first full stop. */
function firstSentence(text: string): string | null {
  const first = text.split('\n').map(line => line.trim()).find(line => line.replace(pictograph, '').trim().length > 0)
  // A table is named by its columns ("Area · To Ostkreuz · To gym"), not by its header row's pipes.
  if (first?.startsWith('|')) return first.split('|').map(cell => cell.trim()).filter(Boolean).join(' · ') || null
  const paragraph = text.split(/\n\s*\n/).map(part => part.split('\n').map(line => line.trim()).filter(Boolean).join(' ')).find(part => part.replace(pictograph, '').trim().length > 0)
  if (!paragraph) return null
  const line = paragraph
  const sentence = /^(.{12,}?[.!?…])(\s|$)/u.exec(line)
  return sentence ? sentence[1]! : line
}

const MAX_EXCERPT = 130
/** The words after a title, without it, run together and cut at a word. */
function excerptOf(text: string, title: string | null): string | null {
  let plain = text.replace(pictograph, '').replace(/[#>*_`]/g, '').replace(/\s+/g, ' ').trim()
  // What follows the title, wherever it sits (a post's author line and kind come before it).
  const at = title ? plain.indexOf(title.replace(/[#>*_`]/g, '').trim()) : -1
  if (title && at >= 0) plain = plain.slice(at + title.replace(/[#>*_`]/g, '').trim().length).replace(/^[\s.:–-]+/, '')
  if (!plain) return null
  if (plain.length <= MAX_EXCERPT) return plain
  const cut = plain.slice(0, MAX_EXCERPT + 1)
  return `${cut.slice(0, Math.max(cut.lastIndexOf(' '), MAX_EXCERPT * 0.6)).replace(/[\s,;:–-]+$/, '')}…`
}

/** The reading's purpose as a label ("Concert ticket"), capitalised, or null. */
const readPurpose = (item: ReadItem) => (item.purpose ? tidyTitle(item.purpose).replace(/^\p{Ll}/u, letter => letter.toUpperCase()) : null)

const GENERIC_PURPOSE_WORDS = new Set(['ticket', 'tickets', 'reservation', 'booking', 'appointment', 'pass', 'stay', 'confirmation', 'event', 'entry'])
/**
 * A booking's purpose from the reading, only when its own words are in the booking: the reading is shown examples ("Dentist appointment") and
 * has copied one onto a Berlin registration office. A purpose naming something the booking never says falls back to its kind.
 */
function groundedPurpose(item: ReadItem): string | null {
  const label = readPurpose(item)
  if (!label) return null
  const evidence = plainWord([item.title, item.text, ...Object.values(item.booking ?? {}).flat().flatMap(part => (typeof part === 'object' && part ? Object.values(part) : [part])), ...item.media.flatMap(media => media.words ?? [])].filter(Boolean).join('\n'))
  const specific = (plainWord(label).match(/\p{L}{4,}/gu) ?? []).filter(word => !GENERIC_PURPOSE_WORDS.has(word))
  return specific.every(word => evidence.includes(word)) ? label : null
}

const day = new Intl.DateTimeFormat(undefined, { weekday: 'short', day: 'numeric', month: 'short' })
const clock = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' })

function cardFor(item: ReadItem, capturedAt: number): Omit<UnpackedCard, 'words'> {
  const platform = item.platform && item.platform.toLowerCase() !== 'web' ? item.platform : null
  const author = item.author.name ?? item.author.handle
  const byline = [author, platform].filter(Boolean).join(' · ') || null
  if (item.booking) {
    // A booking says what it is for, where and when: "Boarding pass" over "BER Terminal 1 → TLV · Sun 25 Oct, 21:25"; an event or a table
    // by its name and day. Its purpose is read from its own words (ticketWords.ts), in any supported language.
    const booking = item.booking
    const date = whenBlock(booking, capturedAt)?.date ?? (booking.when ? readDate(booking.when, capturedAt) : null)
    const start = bookingStart(booking, capturedAt)
    const dayText = date ? day.format(new Date(date.y, date.m, date.d, 12)) : null
    const timeText = start?.time ? clock.format(new Date(2000, 0, 1, Number(start.time.slice(0, 2)), Number(start.time.slice(3)))) : null
    const when = date ? [dayText, timeText].filter(Boolean).join(', ') : booking.when
    const whenLine = date ? [dayText, timeText].filter(Boolean).join(' · ') : booking.when
    const startsAt = date ? new Date(date.y, date.m, date.d, start?.time ? Number(start.time.slice(0, 2)) : 12, start?.time ? Number(start.time.slice(3)) : 0).getTime() : null
    const purpose = bookingPurpose({ ...item, booking })
    const ends = purpose && purpose !== 'hotel' ? routeEnds(booking.where) ?? routeEnds(booking.what) : null
    const route = ends ? `${ends[0]} → ${ends[1]}` : null
    const what = route ?? booking.what ?? booking.with ?? item.title
    const title = [what ? tidyTitle(what) : null, when].filter(Boolean).join(' · ') || null
    // The preview leads with the name; a route leads with its day and time, since the route is on the cover. Seats, gates, guests and the
    // like stay in the note.
    const name = booking.what ?? booking.with ?? item.title
    const venue = item.kind === 'ticket' && booking.with && booking.with !== booking.what ? booking.with : null
    const lead = {
      heading: ends ? whenLine : name ? tidyTitle(name) : null,
      line: ends ? null : [venue ? tidyTitle(venue) : null, whenLine].filter(Boolean).join(' · ') || null,
      route: ends ? { from: routeEnd(ends[0]), to: routeEnd(ends[1]) } : null,
      startsAt,
    }
    // Code names the purpose when the booking's words are clear; otherwise the reading's own word for it (read-v13), else the kind.
    return { kind: item.kind, label: purpose ? PURPOSE_LABEL[purpose] : groundedPurpose(item) ?? LABEL[item.kind], title, detail: when, platform, purpose, ...lead }
  }
  if (item.kind === 'recipe') {
    const title = item.title ? tidyTitle(item.title) : author ? `A recipe from ${author}` : null
    return { kind: 'recipe', label: LABEL.recipe, title, detail: byline, platform }
  }
  const pictureWords = item.media.flatMap(media => media.words ?? [])
  const own = item.text.split('\n').filter(line => !pictureWords.some(word => word.trim() && line.includes(word.trim()))).join('\n')
  // A post that is only the words on its picture is a quote card: its title is the quote.
  if (pictureWords.length && !firstSentence(own)) {
    const named = pictureWords.length > 1 && pictureWords.at(-1)!.split(/\s+/).length <= 6 && !/[.!?…,;:]$/u.test(pictureWords.at(-1)!)
    const quote = (named ? pictureWords.slice(0, -1) : pictureWords).join(' ')
    return { kind: 'quote', label: LABEL.quote, title: tidyTitle(quote), detail: named ? pictureWords.at(-1)!.replace(/^[\s—–-]+/u, '') : byline, platform }
  }
  // Words closed by "— name" (a quote card read as text): the words are the title and the name says who.
  const lines = (own.split(/\n\s*\n/).find(part => part.trim()) ?? '').split('\n').map(line => line.trim()).filter(Boolean)
  const signed = lines.length > 1 && /^[-–—~]\s*\S/u.test(lines.at(-1)!) && lines.at(-1)!.split(/\s+/).length <= 5
  if (signed && !item.title && item.kind === 'post') {
    return { kind: 'quote', label: LABEL.quote, title: tidyTitle(lines.slice(0, -1).join(' ')), detail: lines.at(-1)!.replace(/^[-–—~\s]+/u, ''), platform }
  }
  const title = item.title ?? firstSentence(own) ?? firstSentence(item.text)
  // A post says what kind of post it is when the reading named it ("Tips list", "Opinion post"), else where it was posted.
  const label = item.kind === 'post' ? readPurpose(item) ?? (platform ? `${platform} post` : LABEL.post) : LABEL[item.kind]
  // A post's own words are its preview; an article's first lines, as a link preview shows them. Saved text with no kind stays plain.
  const excerpt = item.kind === 'post' || item.kind === 'article' || item.kind === 'other' ? excerptOf(own || item.text, title) : null
  return { kind: item.kind, label, title: title ? tidyTitle(title) : null, detail: byline, platform, ...(excerpt ? { excerpt } : {}), ...(item.kind === 'other' ? { plain: true } : {}) }
}

/** One item's card, for the head of its note (CleanCard): the same kind, name and line the note's Browse card shows for its first item. */
export function cardOfItem(item: ReadItem, capturedAt: number): UnpackedCard {
  return { ...cardFor(item, capturedAt), words: '' }
}

/** What search finds an unpacked note by: its items' own words, read from the screenshots, never written into the note. */
export function searchWords(items: readonly ReadItem[]): string {
  return items.flatMap(item => [
    item.title, item.author.name, item.author.handle, item.platform, item.text, ...item.tags,
    ...item.media.flatMap(media => media.words ?? []),
    ...(item.booking ? [item.booking.what, item.booking.with, item.booking.when, item.booking.where, item.booking.reference, item.booking.people, ...item.booking.seats] : []),
  ]).filter((part): part is string => Boolean(part && part.trim())).join('\n').replace(pictograph, '').slice(0, 20_000)
}

export function cardOf(run: CleanupRun): UnpackedCard | null {
  const items = run.read?.result?.items ?? []
  if (!items.length || run.undoneAt) return null
  const capturedAt = Math.max(0, ...Object.values(run.signals).map(signal => signal.capturedAt ?? 0)) || run.createdAt
  const checked = rechecked(run, capturedAt)
  const kept = checked?.items.length ? checked.items : items
  const first = (checked ? withoutTextSlides(checked) : null)?.items[0] ?? items[0]!
  // Turned into an editable note, the words are the note's own text: searched and shown in Browse as the person's words, not twice.
  const card = { ...cardFor(first, capturedAt), words: run.own?.editable ? '' : searchWords(kept) }
  return kept.length > 1 ? { ...card, label: `${card.label} + ${kept.length - 1} more` } : card
}

/** Each note's card from its newest run that has a reading. */
export function cardsByNote(runs: readonly CleanupRun[]): Map<string, UnpackedCard> {
  const cards = new Map<string, UnpackedCard>()
  const seen = new Set<string>()
  for (const run of [...runs].sort((a, b) => b.createdAt - a.createdAt)) {
    if (seen.has(run.noteId) || !run.read?.result) continue
    seen.add(run.noteId)
    const card = cardOf(run)
    if (card) cards.set(run.noteId, card)
  }
  return cards
}

export const sameCard = (a: UnpackedCard | undefined | null, b: UnpackedCard | undefined | null) =>
  (a?.kind ?? '') === (b?.kind ?? '') && (a?.title ?? '') === (b?.title ?? '') && (a?.detail ?? '') === (b?.detail ?? '') && (a?.label ?? '') === (b?.label ?? '') && (a?.words ?? '') === (b?.words ?? '') && (a?.purpose ?? null) === (b?.purpose ?? null)

const PLATFORM_ICON: Record<string, IconName> = { instagram: 'instagram', x: 'x', twitter: 'x', facebook: 'facebook', reddit: 'reddit', tiktok: 'tiktok' }
// One glyph per kind, shared with link categories (links/linkPreviewCategory.ts); the clock is the footer's edited time, never a kind.
const KIND_ICON: Record<CardKind, IconName> = { post: 'post', article: 'article', recipe: 'recipe', ticket: 'ticket', reservation: 'calendar', appointment: 'calendar', other: 'document', quote: 'quote' }

/** The card's mark: the kind's own icon, and for a post the platform's when it is one the app knows. */
export function cardIcon(card: UnpackedCard): IconName {
  if (card.purpose === 'boarding-pass' || card.purpose === 'flight') return 'flight'
  if (card.purpose === 'hotel') return 'hotel'
  if (card.purpose === 'ride') return 'car'
  return (card.kind === 'post' && card.platform && PLATFORM_ICON[card.platform.toLowerCase()]) || KIND_ICON[card.kind]
}
